import { useState } from "react";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import ClaimBatches from "../components/ClaimBatches";
import RateCards from "../components/RateCards";

interface Entry {
  id: number; startsAt: string; endsAt: string; kind: string; status: string;
  participantName: string; workerName: string;
}
interface Line {
  id: number; supportItemNumber: string; serviceDate: string; band: string;
  unit: string; quantity: string; unitPriceCents: number; capApplied: boolean; claimType: string;
}
interface Invoice {
  id: number; status: string; totalCents: number; participantName: string; lines: Line[];
  claimBatchId?: number | null;
}
interface ShiftOpt {
  id: number; startsAt: string; endsAt: string;
  participants: { id: number; name: string }[];
  workers: { id: number; name: string }[];
}

const bandLabel: Record<string, string> = {
  weekday_day: "Weekday day", weekday_evening: "Weekday evening", weekday_night: "Weekday night",
  saturday: "Saturday", sunday: "Sunday", public_holiday: "Public holiday", sleepover: "Sleepover",
};
const aud = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export default function Billing() {
  const pending = useFetch<Entry[]>("/v1/billing/timesheet-entries?status=submitted");
  const invoices = useFetch<Invoice[]>("/v1/billing/invoices");
  const shifts = useFetch<ShiftOpt[]>("/v1/rostering/shifts");
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEntry, setShowEntry] = useState(false);
  const [entryForm, setEntryForm] = useState({ shiftId: "" });
  const [entryError, setEntryError] = useState<string | null>(null);

  async function addEntry(e: React.FormEvent) {
    e.preventDefault();
    const shift = (shifts.data ?? []).find((s) => s.id === Number(entryForm.shiftId));
    if (!shift || shift.workers.length === 0) { setEntryError("Pick a shift with an assigned worker"); return; }
    try {
      await api("/v1/billing/timesheet-entries", {
        method: "POST",
        body: JSON.stringify({
          shiftId: shift.id, workerId: shift.workers[0]!.id,
          startsAt: shift.startsAt, endsAt: shift.endsAt,
        }),
      });
      setShowEntry(false); setEntryError(null); pending.reload();
    } catch (err) { setEntryError((err as Error).message); }
  }

  async function approve(id: number) {
    setBusy(id); setError(null);
    try {
      await api(`/v1/billing/timesheet-entries/${id}/approve`, { method: "POST", body: "{}" });
      pending.reload(); invoices.reload();
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(null); }
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-ink">Billing</h1>
        <p className="text-sm text-mut">Approved timesheets rate automatically into NDIS invoice lines.</p>
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-ink">Pending approval</h2>
          <button onClick={() => setShowEntry((v) => !v)}
            className="btn-secondary px-3 py-1.5">
            {showEntry ? "Cancel" : "Add entry from shift"}
          </button>
        </div>
        {showEntry && (
          <form onSubmit={addEntry} className="card p-4 flex flex-wrap gap-3 items-end">
            <label className="block grow">
              <span className="label">Shift (bills scheduled times as worked)</span>
              <select value={entryForm.shiftId} onChange={(e) => setEntryForm({ shiftId: e.target.value })}
                className="input">
                <option value="">Select a shift…</option>
                {(shifts.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.startsAt.replace("T", " ")} → {s.endsAt.slice(11)} · {s.participants.map((p) => p.name).join(", ")} · {s.workers.map((w) => w.name).join(", ")}
                  </option>
                ))}
              </select>
            </label>
            <button className="btn-dark">Add</button>
            {entryError && <p className="text-sm text-red-600 w-full">{entryError}</p>}
          </form>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {pending.data && pending.data.length === 0 && (
          <p className="text-sm text-faint card p-5">Nothing waiting. Entries appear here when workers clock out or managers add them.</p>
        )}
        {pending.data?.map((e) => (
          <div key={e.id} className="card p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-ink">{e.participantName} <span className="text-faint">·</span> {e.workerName}</p>
              <p className="text-xs text-mut font-mono">{e.startsAt.replace("T", " ")} → {e.endsAt.replace("T", " ")}{e.kind === "sleepover" ? " · sleepover" : ""}</p>
            </div>
            <button onClick={() => approve(e.id)} disabled={busy === e.id}
              className="btn-primary">
              {busy === e.id ? "Rating…" : "Approve"}
            </button>
          </div>
        ))}
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-medium text-ink">Invoices</h2>
        {invoices.data && invoices.data.length === 0 && (
          <p className="text-sm text-faint card p-5">No invoices yet — approve a timesheet above.</p>
        )}
        {invoices.data?.map((inv) => (
          <div key={inv.id} className="card overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-edge-soft">
              <p className="text-sm font-medium text-ink">INV-{String(inv.id).padStart(4, "0")} · {inv.participantName}</p>
              <div className="flex items-center gap-3">
                <span className="chip bg-slate-100 text-body">{inv.status}</span>
                <span className="text-sm font-semibold text-ink">{aud(inv.totalCents)}</span>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="th py-2">Support item</th>
                  <th className="th px-2 py-2">Band</th>
                  <th className="th px-2 py-2">Date</th>
                  <th className="th px-2 py-2 text-right">Qty</th>
                  <th className="th px-2 py-2 text-right">Rate</th>
                  <th className="th py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {inv.lines.map((l) => (
                  <tr key={l.id} className="border-t border-edge-soft">
                    <td className="px-5 py-2 font-mono text-xs text-body">{l.supportItemNumber}</td>
                    <td className="px-2 py-2 text-body">{bandLabel[l.band] ?? l.band}{l.capApplied ? " · capped" : ""}</td>
                    <td className="px-2 py-2 text-mut">{l.serviceDate}</td>
                    <td className="px-2 py-2 text-right">{Number(l.quantity).toFixed(2)}{l.unit === "H" ? "h" : ""}</td>
                    <td className="px-2 py-2 text-right text-body">{aud(l.unitPriceCents)}</td>
                    <td className="px-5 py-2 text-right font-medium text-ink">{aud(Math.round(Number(l.quantity) * l.unitPriceCents))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </section>

      <ClaimBatches invoices={invoices.data ?? []} onChange={invoices.reload} />

      <RateCards />
    </div>
  );
}
